import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
// import BionicLogo from 'components/BionicUI/bionicLogo/BionicLogo';
import BPGradientBox from 'components/BionicUI/box/BPGradientBox';
import BPButton from 'components/BionicUI/button/BPButton';
import BPIcon from 'components/BionicUI/icons/BPIcon';
import BPLinkButton from 'components/BionicUI/linkButton/BPLinkButton';
import BackgroundVideo from 'components/onboarding/BackgroundVideo';
import WarningBar from 'components/onboarding/WarningBar';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes';
import { usePrivy } from '@privy-io/react-auth';
import { OnboardingPhases } from 'core/models/users/user.model';
import { MAP_ONBOARDING_PHASE_TO_ROUTE } from 'utils';
import { useAuth } from 'context/auth.context';
import BPLogoLoader from 'components/BionicUI/loading/BPLogoLoader';

interface LandingProps {}

const Landing: FC<LandingProps> = () => {
  const navigate = useNavigate();
  const { user, ready, authenticated } = usePrivy();
  const { handleProfileCreationOrLoad } = useAuth();
  const [loading, setLoading] = useState(false);

  useGSAP(() => {
    const tl = gsap.timeline();
    tl.from('.grid > *', {
      opacity: 0,
      y: 20,
      duration: 0.25,
      stagger: 0.05,
      ease: 'power3.out',
    });
  }, []);

  const handleSignup = async () => {
    navigate(ROUTES.ONBOARDING);
  };

  const handleLogin = async () => {
    navigate(ROUTES.LOGIN);
  };

  useEffect(() => {
    if (user && authenticated) {
      checkUserStatus();
    }
  }, [user, authenticated]);

  const checkUserStatus = async () => {
    setLoading(true);
    try {
      const response = await handleProfileCreationOrLoad();
      if (!!response) {
        if (response.onboardingPhase !== OnboardingPhases.SIGNUP) {
          setLoading(true);
          if (
            response.roles?.includes('PROJECT_OWNER') &&
            response.onboardingPhase === OnboardingPhases.DONE
          ) {
            navigate(ROUTES.APPLICATION_IN_REVIEW);
          } else {
            navigate(MAP_ONBOARDING_PHASE_TO_ROUTE[response.onboardingPhase]);
          }
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.log('Error--', error);
      setLoading(false);
    }
  };
  // console.log('loading--', loading);
  return (
    <div className="grid w-full h-full place-content-center">
      {ready && !loading ? (
        <>
          <BackgroundVideo muted={true} />

          <div className="grid grid-rows-[auto_1fr] gap-[6vh] absolute top-0 left-0 w-screen h-full text-center justify-center page-side-padding">
            <WarningBar />
            <div className="grid relative w-full h-full justify-center items-center page-side-padding">
              <div className="grid relative w-auto h-auto gap-[6vh] justify-center items-center">
                <div className="grid relative w-full justify-center -mt-[3vh]">
                  <img
                    src="/images/ground-zero-logo.svg"
                    alt=""
                    className="h-[42px] w-[174px] object-none"
                  />
                  {/* <BionicLogo
                    className={'w-[20vw] min-w-[150px]'}
                    animate={true}
                  /> */}
                </div>
                <h1 className="grid relative w-full justify-center text-xl sm:text-[24px]">
                  Welcome to Ground Zero.
                  <br />
                  Sign up now to submit your project application.
                </h1>
                <div className="grid relative w-full justify-center gap-3">
                  <Conditions />
                  <div className="block relative w-full justify-center">
                    <BPButton state="primary" onClick={handleSignup}>
                      Start application process
                    </BPButton>
                  </div>
                  <div className="block relative w-full justify-center mt-4">
                    <BPLinkButton className="mx-auto" onClick={handleLogin}>
                      Already a member? Log in
                    </BPLinkButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <BPLogoLoader className="w-[30vw] sm:w-[20vw] lg:w-[15vw] xl:w-[10vw]" />
      )}
    </div>
  );
};

const Conditions: FC = () => (
  <div className="grid relative w-full max-w-[520px] py-[30px] px-[70px] gap-5">
    <BPGradientBox twGradientFromTo="from-bionic-red/50 to-bionic-red/10" />
    <h2 className="text-[16px]">
      To apply to raise funding on Ground Zero you will need:
    </h2>
    <div className="grid relative justify-center gap-3">
      <Condition text="To create an account" />
      <Condition text="Provide some simple information about your project" />
    </div>
  </div>
);

const Condition: FC<{ text: string }> = ({ text }) => (
  <div className="grid relative grid-cols-[auto_1fr] gap-4 items-center">
    <div className="grid relative w-[32px] h-[32px] leading-none justify-center items-center border rounded-full border-bionic-red/40">
      <BPIcon icon="check" className="fill-bionic-green" />
    </div>
    <div className="text-bionic-muted text-[14px]">{text}</div>
  </div>
);

export default Landing;
